import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

function KnowledgeGraph({ processedActors = [] }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [filtroNarrativa, setFiltroNarrativa] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const narrativas = Array.from(new Set([
    ...processedActors.flatMap(a =>
      Array.isArray(a.narrativas) ? a.narrativas : []
    ),
    ...processedActors
      .map(a => a.narrativaDominante)
      .filter(Boolean)
  ])).filter(n =>
    n &&
    n !== 'Narrativa territorial en observación' &&
    n !== 'Sin narrativa declarada'
  ).sort();

  const actoresFiltrados = filtroNarrativa
    ? processedActors.filter(a => {
        const narrs = Array.isArray(a.narrativas) ? a.narrativas : [];
        const dominante = a.narrativaDominante || '';
        return narrs.includes(filtroNarrativa) || dominante === filtroNarrativa;
      })
    : processedActors;

  // Actores que sostienen la narrativa seleccionada — para el panel
  const actoresDeLaNarrativa = filtroNarrativa
    ? processedActors.filter(a => {
        const narrs = Array.isArray(a.narrativas) ? a.narrativas : [];
        const dominante = a.narrativaDominante || '';
        return narrs.includes(filtroNarrativa) || dominante === filtroNarrativa;
      })
    : [];

  useEffect(() => {
    if (!ready || !svgRef.current || !containerRef.current) return;
    if (actoresFiltrados.length < 2) return;

    const width = containerRef.current.clientWidth || 700;
    const height = 500;

    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#071a2f')
      .attr('rx', 18);

    const nodes = actoresFiltrados.map(actor => ({
      id: actor.id,
      nombre: actor.nombre || '',
      ivt: actor._vita?.scoring?.IVT || 0,
      estado: actor._vita?.state?.state || 'Estable',
      narrativa: actor._vita?.reasoning?.narrativaDominante || '',
      centralidad: actor._vita?.relacional?.centralidad || 0,
      esPuente: actor._vita?.relacional?.esPuente || false,
      grado: actor._vita?.relacional?.grado || 0,
      riesgo: actor.riesgo || '',
      influencia: actor.influencia || ''
    }));

    const linksMap = new Map();
    actoresFiltrados.forEach(actor => {
      const relacional = actor._vita?.relacional;
      if (!relacional) return;
      relacional.actoresConectados.forEach(conexion => {
        const targetExists = actoresFiltrados.find(a => a.id === conexion.actorId);
        if (!targetExists) return;
        const key = [actor.id, conexion.actorId].sort().join('--');
        if (!linksMap.has(key)) {
          linksMap.set(key, {
            source: actor.id,
            target: conexion.actorId,
            tipo: conexion.tipo,
            intensidad: conexion.intensidad
          });
        }
      });
    });

    const links = Array.from(linksMap.values());

    const defs = svg.append('defs');
    const filter = defs.append('filter').attr('id', 'glow');
    filter.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'coloredBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links)
        .id(d => d.id)
        .distance(d => d.intensidad === 'Alta' ? 120 : d.intensidad === 'Media' ? 160 : 200)
      )
      .force('charge', d3.forceManyBody().strength(-280))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => getNodeRadius(d) + 12));

    const link = svg.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => getLinkColor(d.tipo))
      .attr('stroke-width', d => d.intensidad === 'Alta' ? 2.5 : d.intensidad === 'Media' ? 1.5 : 1)
      .attr('stroke-opacity', 0.7)
      .attr('stroke-dasharray', d => d.tipo === 'Tensión' ? '5,4' : null);

    const node = svg.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .style('cursor', 'pointer')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x; d.fy = d.y;
        })
        .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null; d.fy = null;
        })
      )
      .on('click', (event, d) => setSelectedNode(d));

    node.filter(d => d.esPuente)
      .append('circle')
      .attr('r', d => getNodeRadius(d) + 6)
      .attr('fill', 'none')
      .attr('stroke', '#fbbf24')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4,3')
      .attr('opacity', 0.8);

    node.append('circle')
      .attr('r', d => getNodeRadius(d))
      .attr('fill', d => getNodeColor(d))
      .attr('stroke', 'rgba(255,255,255,0.2)')
      .attr('stroke-width', 2)
      .attr('filter', d => d.ivt >= 70 ? 'url(#glow)' : null);

    node.append('text')
      .text(d => {
        const words = d.nombre.split(' ');
        return words.length > 2 ? words.slice(0, 2).join(' ') + '...' : d.nombre;
      })
      .attr('text-anchor', 'middle')
      .attr('dy', d => getNodeRadius(d) + 14)
      .attr('fill', 'rgba(255,255,255,0.85)')
      .attr('font-size', '11px')
      .attr('font-weight', '700')
      .attr('font-family', 'Arial');

    node.append('text')
      .text(d => d.ivt)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', 'white')
      .attr('font-size', d => getNodeRadius(d) > 20 ? '12px' : '10px')
      .attr('font-weight', '900')
      .attr('font-family', 'Arial');

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x).attr('y2', d => d.target.y);

      node.attr('transform', d => {
        const r = getNodeRadius(d) + 10;
        d.x = Math.max(r, Math.min(width - r, d.x));
        d.y = Math.max(r, Math.min(height - r, d.y));
        return `translate(${d.x},${d.y})`;
      });
    });

    return () => simulation.stop();
  }, [ready, actoresFiltrados]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.badge}>KNOWLEDGE GRAPH</div>
          <h2 style={styles.title}>Grafo relacional territorial</h2>
          <p style={styles.subtitle}>
            Red cognitiva de actores detectada desde convergencia narrativa
          </p>
        </div>
      </div>

      <div style={styles.leyenda}>
        <div style={styles.leyendaGrupo}>
          <span style={styles.leyendaLabel}>RELACIONES</span>
          <LeyendaItem color="#ef4444" label="Convergencia" />
          <LeyendaItem color="#22c55e" label="Aliado" />
          <LeyendaItem color="#f97316" label="Tensión" dashed />
          <LeyendaItem color="#67e8f9" label="Coordinación" />
        </div>
        <div style={styles.leyendaGrupo}>
          <span style={styles.leyendaLabel}>NODOS</span>
          <LeyendaItem color="#ef4444" label="Alto IVT" circle />
          <LeyendaItem color="#eab308" label="IVT medio" circle />
          <LeyendaItem color="#22c55e" label="IVT bajo" circle />
          <LeyendaItem color="#fbbf24" label="Actor puente" dashed circle />
        </div>
      </div>

      {/* FILTROS NARRATIVA */}
      {narrativas.length > 0 && (
        <div style={styles.filtros}>
          <button
            style={{ ...styles.filtroBtn, ...(filtroNarrativa === '' ? styles.filtroBtnActive : {}) }}
            onClick={() => { setFiltroNarrativa(''); setSelectedNode(null); }}
          >
            Todas
          </button>
          {narrativas.map(n => (
            <button
              key={n}
              style={{ ...styles.filtroBtn, ...(filtroNarrativa === n ? styles.filtroBtnActive : {}) }}
              onClick={() => { setFiltroNarrativa(n === filtroNarrativa ? '' : n); setSelectedNode(null); }}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {/* PANEL — quién sostiene la narrativa seleccionada */}
      {filtroNarrativa && actoresDeLaNarrativa.length > 0 && (
        <div style={styles.narrativaPanel}>
          <div style={styles.narrativaPanelLabel}>
            ACTORES QUE SOSTIENEN ESTA NARRATIVA
          </div>
          <div style={styles.narrativaPanelTitulo}>
            "{filtroNarrativa}"
          </div>
          <div style={styles.narrativaPanelActores}>
            {actoresDeLaNarrativa.map((actor, i) => (
              <div key={i} style={styles.narrativaPanelActor}>
                <div style={styles.narrativaPanelActorNombre}>{actor.nombre}</div>
                <div style={styles.narrativaPanelActorMeta}>
                  <span style={{
                    ...styles.influenciaTag,
                    background: actor.influencia === 'Alta'
                      ? 'rgba(239,68,68,0.15)'
                      : actor.influencia === 'Media'
                      ? 'rgba(234,179,8,0.15)'
                      : 'rgba(148,163,184,0.15)',
                    color: actor.influencia === 'Alta'
                      ? '#f87171'
                      : actor.influencia === 'Media'
                      ? '#fbbf24'
                      : '#94a3b8'
                  }}>
                    Influencia {actor.influencia || '—'}
                  </span>
                  <span style={styles.ivtTagSmall}>
                    IVT {actor._vita?.scoring?.IVT || '—'}%
                  </span>
                  <span style={styles.estadoTagSmall}>
                    {actor._vita?.state?.state || actor.estado || '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {actoresDeLaNarrativa.length === 1 && (
            <div style={styles.narrativaSolitaria}>
              ⚠ Narrativa emergente — solo 1 actor la sostiene. Monitorear si se expande.
            </div>
          )}
          {actoresDeLaNarrativa.length >= 2 && (
            <div style={styles.narrativaConvergente}>
              ✓ Convergencia detectada — {actoresDeLaNarrativa.length} actores comparten esta narrativa.
            </div>
          )}
        </div>
      )}

      {/* GRAFO */}
      <div ref={containerRef} style={styles.grafoWrapper}>
        {actoresFiltrados.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>🕸️</div>
            <div style={styles.emptyTitle}>Sin actores con esta narrativa</div>
            <div style={styles.emptyText}>No hay actores registrados con esta narrativa.</div>
          </div>
        ) : actoresFiltrados.length === 1 ? (
          <div style={styles.actorUnico}>
            <div style={styles.actorUnicoLabel}>NARRATIVA EMERGENTE — 1 ACTOR</div>
            <div style={styles.actorUnicoNombre}>{actoresFiltrados[0].nombre}</div>
            <div style={styles.actorUnicoMeta}>
              {actoresFiltrados[0].sector || actoresFiltrados[0].comuna || '—'} ·
              Influencia {actoresFiltrados[0].influencia || '—'} ·
              IVT {actoresFiltrados[0]._vita?.scoring?.IVT || '—'}%
            </div>
            <div style={styles.actorUnicoNota}>
              Solo un actor sostiene esta narrativa. Monitorear si se expande a otros actores.
            </div>
          </div>
        ) : (
          <svg ref={svgRef} style={{ width: '100%', height: '500px', display: 'block', borderRadius: '18px' }} />
        )}
      </div>

      {/* PANEL NODO SELECCIONADO */}
      {selectedNode && (
        <div style={styles.nodePanel}>
          <div style={styles.nodePanelHeader}>
            <div style={styles.nodePanelNombre}>{selectedNode.nombre}</div>
            <button style={styles.nodePanelClose} onClick={() => setSelectedNode(null)}>✕</button>
          </div>
          <div style={styles.nodePanelGrid}>
            <NodeStat label="IVT" value={`${selectedNode.ivt}%`} />
            <NodeStat label="Centralidad" value={`${selectedNode.centralidad}%`} />
            <NodeStat label="Conexiones" value={selectedNode.grado} />
            <NodeStat label="Estado" value={selectedNode.estado} />
          </div>
          <div style={styles.nodePanelNarrativa}>
            <div style={styles.nodePanelNarrativaLabel}>NARRATIVA DOMINANTE</div>
            <div style={styles.nodePanelNarrativaValue}>{selectedNode.narrativa || 'Sin narrativa declarada'}</div>
          </div>
          {selectedNode.esPuente && (
            <div style={styles.puenteBadge}>⚡ Actor puente territorial</div>
          )}
        </div>
      )}
    </div>
  );
}

function LeyendaItem({ color, label, dashed, circle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {circle ? (
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color, flexShrink: 0 }} />
      ) : (
        <div style={{ width: '20px', height: '0', borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}`, flexShrink: 0 }} />
      )}
      <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '11px' }}>{label}</span>
    </div>
  );
}

function NodeStat({ label, value }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
      <div style={{ color: '#67e8f9', fontSize: '16px', fontWeight: '900' }}>{value}</div>
      <div style={{ color: '#94a3b8', fontSize: '10px', marginTop: '3px' }}>{label}</div>
    </div>
  );
}

function getNodeRadius(d) {
  const base = 16;
  const porCentralidad = (d.centralidad / 100) * 12;
  const porIVT = (d.ivt / 100) * 8;
  return Math.round(base + porCentralidad + porIVT);
}

function getNodeColor(d) {
  if (d.ivt >= 71) return '#ef4444';
  if (d.ivt >= 51) return '#eab308';
  if (d.ivt >= 31) return '#60a5fa';
  return '#22c55e';
}

function getLinkColor(tipo) {
  if (tipo === 'Convergencia') return '#ef4444';
  if (tipo === 'Aliado') return '#22c55e';
  if (tipo === 'Tensión') return '#f97316';
  return '#67e8f9';
}

const styles = {
  container: {
    background: 'radial-gradient(circle at top left,rgba(31,169,122,0.08),transparent 30%), linear-gradient(135deg,#071a2f,#0b2442)',
    borderRadius: '26px',
    padding: '28px',
    marginBottom: '34px',
    border: '1px solid rgba(255,255,255,0.07)',
    boxShadow: '0 20px 45px rgba(0,0,0,0.22)'
  },
  header: { marginBottom: '20px' },
  badge: {
    display: 'inline-block', padding: '5px 12px', borderRadius: '999px',
    background: 'rgba(103,232,249,0.12)', color: '#67e8f9', fontSize: '10px',
    fontWeight: '900', letterSpacing: '1px', marginBottom: '10px',
    border: '1px solid rgba(103,232,249,0.22)', textTransform: 'uppercase'
  },
  title: { margin: 0, color: 'white', fontSize: '24px' },
  subtitle: { color: 'rgba(255,255,255,0.55)', marginTop: '6px', fontSize: '13px' },
  leyenda: {
    display: 'flex', gap: '32px', flexWrap: 'wrap', marginBottom: '16px',
    padding: '14px', background: 'rgba(255,255,255,0.03)',
    borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)'
  },
  leyendaGrupo: { display: 'flex', flexDirection: 'column', gap: '8px' },
  leyendaLabel: {
    color: '#94a3b8', fontSize: '10px', fontWeight: '900',
    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px'
  },
  filtros: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' },
  filtroBtn: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)', borderRadius: '999px', padding: '8px 14px',
    cursor: 'pointer', fontSize: '12px', fontWeight: '700'
  },
  filtroBtnActive: {
    background: 'linear-gradient(135deg,#0891b2,#0f766e)',
    color: 'white', border: '1px solid transparent'
  },
  narrativaPanel: {
    background: 'rgba(103,232,249,0.06)',
    border: '1px solid rgba(103,232,249,0.18)',
    borderRadius: '16px',
    padding: '18px',
    marginBottom: '16px'
  },
  narrativaPanelLabel: {
    color: '#94a3b8', fontSize: '10px', fontWeight: '900',
    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px'
  },
  narrativaPanelTitulo: {
    color: '#67e8f9', fontSize: '16px', fontWeight: '700',
    fontStyle: 'italic', marginBottom: '14px', lineHeight: '1.4'
  },
  narrativaPanelActores: {
    display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px'
  },
  narrativaPanelActor: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '10px', padding: '10px 14px'
  },
  narrativaPanelActorNombre: {
    color: 'white', fontSize: '14px', fontWeight: '700', marginBottom: '4px'
  },
  narrativaPanelActorMeta: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  influenciaTag: {
    padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '700'
  },
  ivtTagSmall: { color: '#67e8f9', fontSize: '11px', fontWeight: '700' },
  estadoTagSmall: { color: 'rgba(255,255,255,0.5)', fontSize: '11px' },
  narrativaSolitaria: {
    background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)',
    color: '#fbbf24', borderRadius: '8px', padding: '8px 12px',
    fontSize: '12px', fontWeight: '600'
  },
  narrativaConvergente: {
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
    color: '#4ade80', borderRadius: '8px', padding: '8px 12px',
    fontSize: '12px', fontWeight: '600'
  },
  grafoWrapper: {
    borderRadius: '18px', overflow: 'hidden', marginBottom: '16px', minHeight: '500px'
  },
  empty: {
    textAlign: 'center', padding: '60px 20px', background: '#071a2f',
    borderRadius: '18px', minHeight: '500px', display: 'flex',
    flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
  },
  emptyIcon: { fontSize: '40px', marginBottom: '14px' },
  emptyTitle: { color: 'white', fontWeight: '800', fontSize: '16px', marginBottom: '8px' },
  emptyText: { color: 'rgba(255,255,255,0.5)', fontSize: '13px' },
  actorUnico: {
    background: '#071a2f', borderRadius: '18px', minHeight: '200px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '40px',
    border: '1px dashed rgba(251,191,36,0.3)'
  },
  actorUnicoLabel: {
    color: '#fbbf24', fontSize: '11px', fontWeight: '900',
    letterSpacing: '1px', marginBottom: '12px'
  },
  actorUnicoNombre: { color: 'white', fontSize: '22px', fontWeight: '900', marginBottom: '8px' },
  actorUnicoMeta: { color: 'rgba(255,255,255,0.55)', fontSize: '13px', marginBottom: '12px' },
  actorUnicoNota: {
    color: 'rgba(255,255,255,0.35)', fontSize: '12px',
    fontStyle: 'italic', textAlign: 'center', maxWidth: '400px'
  },
  nodePanel: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(103,232,249,0.18)',
    borderRadius: '16px', padding: '18px'
  },
  nodePanelHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '14px'
  },
  nodePanelNombre: { color: 'white', fontSize: '18px', fontWeight: '900' },
  nodePanelClose: {
    background: 'rgba(255,255,255,0.08)', border: 'none', color: 'white',
    borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', fontSize: '14px'
  },
  nodePanelGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '14px'
  },
  nodePanelNarrativa: {
    background: 'rgba(103,232,249,0.08)',
    border: '1px solid rgba(103,232,249,0.18)',
    borderRadius: '10px', padding: '12px', marginBottom: '10px'
  },
  nodePanelNarrativaLabel: {
    color: '#94a3b8', fontSize: '10px', fontWeight: '800',
    textTransform: 'uppercase', marginBottom: '6px'
  },
  nodePanelNarrativaValue: { color: '#67e8f9', fontSize: '14px', fontWeight: '700' },
  puenteBadge: {
    background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)',
    color: '#fbbf24', borderRadius: '8px', padding: '8px 12px',
    fontSize: '12px', fontWeight: 'bold'
  }
};

export default KnowledgeGraph;